import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { CommonButton } from "./Components/common/commonButton";
import { logout, setIsSessionExpired } from "./redux/slice/loginSlice";

export default function TimeoutProvider({ children }) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);

  const { isLoggedIn } = useSelector((state) => state?.login);
  const location = useLocation();
  const dispatch = useDispatch();

  const sessionTimeOutRef = useRef();
  const sessionWarningTimeOutRef = useRef();
  const countdownIntervalRef = useRef();

  const sessionTimeOutTime = Number(process.env.REACT_APP_IDLE_SESSION_TIMEOUT); // e.g., 600000
  const sessionWarningTime = Number(process.env.REACT_APP_IDLE_SESSION_WARNING_TIMEOUT); // e.g., 540000

  const eventsList = [
    "mousedown",
    "mousemove",
    "click",
    "wheel",
    "keydown",
    "touchstart",
    "scroll",
  ];

  const exemptedRoutes = ["/", "/login", "/forget-password"];

  const broadcastActivity = () => {
    localStorage.setItem("user-activity", Date.now().toString());
  };

  const broadcastLogout = () => {
    localStorage.setItem("force-logout", Date.now().toString());
  };

  const clearSessionTimers = () => {
    clearTimeout(sessionTimeOutRef.current);
    clearTimeout(sessionWarningTimeOutRef.current);
    clearInterval(countdownIntervalRef.current);
  };

  const handleManualLogout = () => {
    setIsDialogOpen(false);
    clearSessionTimers();
    broadcastLogout();
    dispatch(logout());
  };

  const handleSessionTimeout = () => {
    setIsDialogOpen(false);
    clearSessionTimers();
    broadcastLogout();
    dispatch(setIsSessionExpired(true));
  };

  const handleStay = () => {
    setIsDialogOpen(false);
    handleWindowEvents();
  };

  const handleWindowEvents = () => {
    if (!isLoggedIn) return;

    clearSessionTimers();
    setRemainingTime(sessionTimeOutTime);

    // Auto logout timer
    sessionTimeOutRef.current = setTimeout(() => {
      handleSessionTimeout();
    }, sessionTimeOutTime);

    // Warning dialog timer
    sessionWarningTimeOutRef.current = setTimeout(() => {
      setIsDialogOpen(true);
    }, sessionWarningTime);

    // Countdown timer
    countdownIntervalRef.current = setInterval(() => {
      setRemainingTime((prev) => {
        if (prev <= 1000) {
          clearInterval(countdownIntervalRef.current);
          return 0;
        }
        return prev - 1000;
      });
    }, 1000);

    broadcastActivity();
  };

  const handleStorageChange = (e) => {
    if (e.key === "force-logout") {
      setIsDialogOpen(false);
      clearSessionTimers();
      dispatch(logout());
    }
    if (e.key === "user-activity") {
      handleWindowEvents();
    }
  };

  useEffect(() => {
    if (exemptedRoutes.includes(location.pathname)) return;

    handleWindowEvents();

    eventsList.forEach((event) =>
      window.addEventListener(event, handleWindowEvents, { passive: true })
    );

    window.addEventListener("storage", handleStorageChange);

    return () => {
      eventsList.forEach((event) =>
        window.removeEventListener(event, handleWindowEvents, { passive: true })
      );
      window.removeEventListener("storage", handleStorageChange);
      clearSessionTimers();
    };
  }, [location.pathname, isLoggedIn]);

  return (
    <>
      {children}

      {isLoggedIn && (
        <div
          style={{
            position: "fixed",
            bottom: 10,
            right: 10,
            backgroundColor: "#fff3cd",
            padding: "8px 12px",
            border: "1px solid #ffeeba",
            borderRadius: 4,
            color: "#856404",
            fontWeight: "bold",
            fontSize: "14px",
            zIndex: 9999,
          }}
        >
          Session expires in:{" "}
          {Math.floor(remainingTime / 60000)}m{" "}
          {Math.floor((remainingTime % 60000) / 1000)}s
        </div>
      )}

      {isDialogOpen && isLoggedIn && (
        <Dialog open={isDialogOpen} fullWidth>
          <DialogTitle>Session Timeout</DialogTitle>
          <DialogContent>
            <Typography>
              Your session is about to expire. Do you want to stay signed in?
            </Typography>
          </DialogContent>
          <DialogActions>
            <CommonButton
              text="Sign out now"
              size="large"
              handleClick={handleManualLogout}
              hoverColor="#FF5A01"
            />
            <CommonButton
              text="Stay signed in"
              size="large"
              handleClick={handleStay}
              hoverColor="#FF5A01"
            />
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}
